(ns opengb.quantity-format
  (:require [opengb.dram.quantity :as q])
  #?(:clj  (:import (com.ibm.icu.number NumberFormatter NumberFormatter$RoundingPriority FractionPrecision Precision)
                    (com.ibm.icu.util ULocale$Builder))))

;; # Docs

;; NumberFormatter
;; https://unicode-org.github.io/icu-docs/apidoc/dev/icu4j/com/ibm/icu/number/NumberFormatter.html

;; MessageFormat
;; https://unicode-org.github.io/icu-docs/apidoc/dev/icu4j/com/ibm/icu/text/MessageFormat

;; ULocale$Builder
;; https://unicode-org.github.io/icu-docs/apidoc/dev/icu4j/com/ibm/icu/util/ULocale.Builder.html

;; messageformatjs
;; http://messageformat.github.io/messageformat/api/core.messageformat/

;; Intl.Locale
;; https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale

;; Intl.NumberFormat
;; https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat

;; Formatting numbers based on locale and specified decimal places

(defn format-number
  "Formats `n` for a locale, rounded to `decimal-places`, to significant digits, or
   to whichever of the two is more precise when both are given."
  [n {:keys [language region decimal-places min-significant-digits max-significant-digits]
      :or {language "en" region "CA"}}]
  (let [significant-digits? (or min-significant-digits max-significant-digits)
        fraction-digits     (if significant-digits? decimal-places (or decimal-places 0))
        ;; ICU (:clj) requires both sig digit bounds; JS Intl (:cljs) lets each be omitted and has defaults.
        ;; These defaults are set to match JS Intl's defaults. See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#maximumsignificantdigits
        min-sig             (or min-significant-digits 1)
        max-sig             (or max-significant-digits 21)]
    #?(:clj  (let [locale (-> (ULocale$Builder.)
                              (.setLanguage language)
                              (.setRegion region)
                              (.build))
                   precision (cond
                               (not significant-digits?)
                               (FractionPrecision/fixedFraction fraction-digits)

                               (nil? fraction-digits)
                               (Precision/minMaxSignificantDigits min-sig max-sig)

                               :else
                               (.withSignificantDigits (FractionPrecision/fixedFraction fraction-digits)
                                                       min-sig
                                                       max-sig
                                                       NumberFormatter$RoundingPriority/RELAXED))]
               (-> (NumberFormatter/withLocale locale)
                   (.precision precision)
                   (.format n)
                   (.toString)))
       :cljs (let [locale (js/Intl.Locale. language (clj->js {:region region}))
                   options (clj->js (cond-> {}
                                      fraction-digits
                                      (assoc :minimumFractionDigits fraction-digits
                                             :maximumFractionDigits fraction-digits)

                                      significant-digits?
                                      (assoc :minimumSignificantDigits min-sig
                                             :maximumSignificantDigits max-sig)

                                      (and fraction-digits significant-digits?)
                                      (assoc :roundingPriority "morePrecision")))
                   nf (js/Intl.NumberFormat locale options)
                   result (.format nf n)]
               result))))

(defn format-number-example
  []
  (format-number 1234.5678 {:language "fr" :decimal-places 2}))

(comment
  (format-number-example))

;; Formatting quantities based on unit, locale, and specified decimal places

(defn create-locale
  [{:keys [language region]
    :or {language "en" region "CA"}
    :as _locale-args}]
  #?(:clj  (-> (ULocale$Builder.)
               (.setLanguage language)
               (.setRegion region)
               (.build))
     :cljs (js/Intl.Locale. language (clj->js {:region region}))))

(defn format-number-for-locale
  [locale {:keys [decimal-places conversion]
           :or {conversion 1}
           :as _formatting-opts}
   n]
  #?(:clj  (let [precision (FractionPrecision/fixedFraction decimal-places)]
             (-> (NumberFormatter/withLocale locale)
                 (.precision precision)
                 (.format (* n conversion))
                 (.toString)))
     :cljs (let [options (clj->js {:minimumFractionDigits decimal-places
                                   :maximumFractionDigits decimal-places})
                 nf (js/Intl.NumberFormat locale options)
                 result (.format nf (* n conversion))]
             result)))

;; These either get defined at OPEN or at org level
(def formatting-opts-for-unit
  {"m**2"       {:decimal-places 3}
   "GJ/year"    {:decimal-places 0}
   "GJ/m²/year" {:decimal-places 2}
   "factor"     {:decimal-places 1}
   "CAD"        {:decimal-places 0}
   "tCO2e"      {:decimal-places 2}
   "ktCO2e"     {:decimal-places 1}
   "%"          {:decimal-places 0
                 :conversion     100}
   "placeholder" {:decimal-places 0}})

;; Gets the formatting options for a unit or provides default
(defn quantity->formatting-opts
  [quantity]
  (let [unit (q/get-unit quantity)
        default {:decimal-places 0}]
    (get formatting-opts-for-unit unit default)))

(defn format-quantity
  ([quantity locale-args]
   (format-quantity quantity locale-args nil))
  ;; Manual formatting opts allow them to be overriden from a specific call site
  ([quantity locale-args manual-formatting-opts]
   (let [locale (create-locale locale-args)
         formatting-opts (or manual-formatting-opts
                             (quantity->formatting-opts quantity))
         mag (q/get-magnitude quantity)]
     (format-number-for-locale locale formatting-opts mag))))

(defn format-quantity-example
  []
  (format-quantity [5.123 "m**2"] {:language "fr"} {:decimal-places 2}))

(comment
  (format-quantity-example)
  (format-quantity [5.123 "ft**2"] {:language "es" :region "US"})
  (format-quantity [5.123 "m**2"] {:language "en"} {:decimal-places 5}))

;; A theoretical Quantity view could now:
;; - take in quantity and optional overrides
;; - convert quantity to current unit system
;; - format mag based on unit, locale, and overrides
;; - translate unit string to displayable string
